// Espelho TS das tabelas do Postgres (migrations 0001-0005).
// Datas viajam como string ISO (JSON do Supabase).
import { novoId } from './novoId'

export interface RegistroBase {
  id: string
  tenant_id?: string // preenchido pelo servidor; ausente em registros criados offline
  criado_em: string
  atualizado_em: string
  apagado_em: string | null
}

export interface Contato {
  nome?: string
  ddd?: string
  telefone?: string
  email?: string
  cargo?: string
  whatsapp?: string
}

export interface Representada extends RegistroBase {
  nome: string
  logo?: string | null // 0033, data URL (spec 2026-10-06)
  cnpj: string | null
  contatos: Contato[]
  prazo_medio_entrega_dias: number | null
  pedido_minimo_valor: number | null
  transportadoras_usuais: string[]
  observacoes: string | null
  // Motor de imposto, 0024. Opcionais no TIPO pelo mesmo motivo de sempre: o
  // sync e por cursor, e conta que ja tinha a representada antes da 0024 le
  // `undefined`. `calcula_imposto` ausente DEVE ler como false, nunca truthy.
  calcula_imposto?: boolean
  uf_origem?: string | null
  // Endereco (0025, pedido dela em 09/09) -- mesmos cinco campos do Cliente,
  // opcionais no tipo pelo mesmo motivo do bloco acima: sync por cursor, quem
  // ja tinha a representada antes da 0025 le `undefined`.
  endereco?: string | null
  bairro?: string | null
  municipio?: string | null
  uf?: string | null
  cep?: string | null
  // Cadastro em 3 abas (0026, spec 2026-09-17). Opcionais no tipo pelo mesmo
  // motivo de sempre: sync por cursor, representada de antes da 0026 le
  // `undefined`, nunca deve ser tratado como erro.
  razao_social?: string | null
  inscricao_estadual?: string | null
  inscricao_municipal?: string | null
  regime_tributario?: RegimeTributario | null
  segmento?: string | null
  numero?: string | null
  complemento?: string | null
  website?: string | null
  instagram?: string | null
  prazo_pagamento_padrao?: string | null
  formas_pagamento_aceitas?: string[]
  // 0027 (spec 27/09): formas acrescentadas pelo [+]; as 6 fixas moram no app.
  formas_pagamento_extras?: string[]
  limite_credito_solicitado?: number | null
  limite_credito_aprovado?: number | null
  frete_padrao?: 'CIF' | 'FOB' | 'REDESPACHO' | null
}

// Spec 2026-10-06: dados da empresa do representante (uma linha por conta,
// 0033). `logo` = data URL comprimida no aparelho (lib/imagem.ts).
export interface Empresa extends RegistroBase {
  razao_social: string | null
  nome_fantasia: string | null
  cnpj: string | null
  email: string | null
  telefone: string | null
  endereco: string | null
  cep: string | null
  municipio: string | null
  uf: string | null
  logo: string | null
}

export interface CondicaoPagamento extends RegistroBase {
  representada_id: string
  descricao: string
  ordem: number
  desconto_pct?: number | null
}

export type RegimeTributario =
  | 'simples_nacional'
  | 'lucro_presumido'
  | 'lucro_real'
  | 'mei'
  | 'isento'

export const REGIMES_TRIBUTARIOS: ReadonlyArray<{ codigo: RegimeTributario; rotulo: string }> = [
  { codigo: 'simples_nacional', rotulo: 'Simples Nacional' },
  { codigo: 'lucro_presumido', rotulo: 'Lucro Presumido' },
  { codigo: 'lucro_real', rotulo: 'Lucro Real' },
  { codigo: 'mei', rotulo: 'MEI' },
  { codigo: 'isento', rotulo: 'Isento / Não contribuinte' },
]

export type MotivoInativacao = 'mal_pagador' | 'fechou' | 'faliu' | 'mudou_ramo' | 'outro'

export interface Cliente extends RegistroBase {
  razao_social: string
  nome_fantasia: string | null
  cnpj_cpf: string | null
  contatos: Contato[]
  endereco: string | null
  bairro: string | null
  municipio: string | null
  uf: string | null
  cep: string | null
  // Endereco de entrega (0036). Todos vazios = idem ao do cliente; regras em
  // clientes/enderecoEntrega.ts. Opcionais so para as fixtures antigas.
  entrega_endereco?: string | null
  entrega_bairro?: string | null
  entrega_municipio?: string | null
  entrega_uf?: string | null
  entrega_cep?: string | null
  regiao: string | null
  segmento: string | null
  inscricao_estadual: string | null
  regime_tributario: RegimeTributario | null
  linha_credito: number | null
  // A=Ouro, B=Prata, C=Bronze, D=Niquel. Os nomes moram em clientes/classificacao.ts.
  potencial: 'A' | 'B' | 'C' | 'D' | null
  dia_visita: number | null
  dia_entrega: number | null
  // Ciclo 2B (spec 2026-10-06). Opcionais so para as fixtures antigas: o
  // servidor sempre manda. Datas sao o dia LOCAL 'AAAA-MM-DD'. As regras
  // moram em visitas/retorno.ts.
  retorno_dias?: number | null
  retorno_desde?: string | null
  retorno_marcado?: string | null
  retorno_aviso_excluido?: string | null
  status: 'ativo' | 'inativo'
  // Por que saiu de circulacao. Guarda a ULTIMA inativacao: reativar preserva os
  // tres campos, inativar de novo sobrescreve. Historico completo seria tabela
  // propria, e e assunto das fases 7 e 8.
  motivo_inativacao: MotivoInativacao | null
  obs_inativacao: string | null
  inativado_em: string | null
  observacoes: string | null
}

export interface ClienteRepresentada extends RegistroBase {
  cliente_id: string
  representada_id: string
  codigo_cliente_na_representada: string | null
  tabela_preco_padrao_id: string | null
  // Migration 0035. true = a pessoa ESCOLHEU esta tabela na ficha do cliente: o
  // pedido nao a regrava (aprenderTabela) e mostra so ela daquela marca
  // (pedidos/tabelaFixa.ts). false/ausente = tabela apenas aprendida do ultimo
  // pedido, que so passa na frente na busca. Opcional por causa do cache antigo.
  tabela_preco_fixa?: boolean
  observacoes: string | null
}

export interface Produto extends RegistroBase {
  representada_id: string
  codigo: string
  descricao: string
  unidade: string | null
  categoria: string | null
  linha: string | null
  // Opcional no TIPO de proposito: aparelho que ja tinha o produto salvo antes
  // da 0019 nao tem a chave, e ela chega como `undefined`. Ver a spec.
  fabricante?: string | null
  // Quantas unidades cabem na embalagem que o campo `unidade` nomeia (CX, FD,
  // dz...). Opcional no TIPO pelo mesmo motivo do `fabricante` e dos fiscais: o
  // sync e por cursor e nunca repuxa linha que nao mudou, entao o aparelho que
  // ja tinha o produto salvo antes da 0021 le `undefined`, e nao `null`.
  // ⚠ NUNCA ler daqui direto -- usar produtos/embalagem.ts. O `1` e dado real
  // (19 produtos da MAVALERIO) e significa "sem embalagem fechada".
  unidades_por_embalagem?: number | null
  // Fiscais da 0020. Opcionais no TIPO pelo mesmo motivo do `fabricante` acima:
  // o sync e por cursor e nunca repuxa linha que nao mudou, entao o aparelho que
  // ja tinha o produto salvo antes da 0020 le `undefined`, e nao `null`.
  // ⚠ Quem ler estes campos usa `!= null` (pega os dois), nunca `!== null`.
  //
  // `ncm` e string porque a coluna e text: familias de NCM comecam com zero
  // (04051000) e numero comeria o zero. Ver a spec.
  ncm?: string | null
  ipi_pct?: number | null
  icms_pct?: number | null
  st_pct?: number | null
  // Fiscal da 0022, mesma regra dos tres acima -- opcional, `!= null` sempre.
  pis_cofins_pct?: number | null
  foto_url: string | null
  estoque: number | null
  observacoes: string | null
  ativo: boolean
}

// Motor de imposto, 0024. `representada_id` e `produto_id`/`uf_destino`
// espelham as tabelas do Postgres 1:1 -- ver a spec 2026-09-06.
export interface AliquotaIcms extends RegistroBase {
  representada_id: string
  produto_id: string
  uf_destino: string
  aliquota_pct: number
}

export interface AliquotaSt extends RegistroBase {
  representada_id: string
  produto_id: string
  uf_destino: string
  mva_pct: number
}

export interface DescontoFrete extends RegistroBase {
  representada_id: string
  regiao_destino: string
  incoterm: 'CIF' | 'FOB' | 'REDESPACHO'
  desconto_pct: number
}

export interface TabelaPreco extends RegistroBase {
  representada_id: string
  nome: string
  vigencia_inicio: string | null
  vigencia_fim: string | null
  ativa: boolean
  // 0027: condicoes em que a tabela vale; vazio/ausente = qualquer condicao.
  condicoes_validas?: string[]
  nome_normalizado?: string
}

export interface Preco extends RegistroBase {
  tabela_preco_id: string
  produto_id: string
  preco: number
  // A regra de quantidade DAQUELE preco: "so vale de 24 unidades para cima".
  // NULO = sem regra, que e o caso de todo preco anterior a 17/08/2026 -- e
  // e o que faz esta feature nao mudar nada para quem nao preencher.
  // Mora aqui, e nao na tabela, porque o minimo muda de produto para produto
  // dentro da mesma tabela. Ver pedidos/regraQuantidade.ts.
  quantidade_minima: number | null
  quantidade_maxima: number | null
  // Quanto custa a unidade SOLTA. Nulo = esta fabrica nao quebra embalagem.
  // Independente do `preco` de proposito (decisao dela, 28/08): quebrar caixa
  // tem custo, e ha fabrica que cobra agio no avulso.
  preco_unidade?: number | null
  // O preco antes do ultimo Salvar na tela Editar da tabela (teste dela 29/09):
  // para o vendedor desfazer um erro de digitacao. Nulo = nunca mudou ali.
  preco_anterior?: number | null
}

export interface PrecoNegociado extends RegistroBase {
  cliente_id: string
  produto_id: string
  preco: number
  vigencia_inicio: string | null
  vigencia_fim: string | null
  // Quanto custa a unidade SOLTA. Nulo = esta fabrica nao quebra embalagem.
  // Independente do `preco` de proposito (decisao dela, 28/08): quebrar caixa
  // tem custo, e ha fabrica que cobra agio no avulso.
  preco_unidade?: number | null
}

export interface Pedido extends RegistroBase {
  cliente_id: string
  numero: string
  data: string
  status: 'aberto' | 'fechado' | 'cancelado'
  observacoes: string | null
}

export interface SubPedido extends RegistroBase {
  pedido_id: string
  representada_id: string
  letra: string
  // 'entregue' entrou no Ciclo 2 (spec 2026-07-29), entre faturado e pago: a
  // mercadoria chegou e falta so o pagamento do cliente.
  status: 'rascunho' | 'fechado' | 'enviado' | 'faturado' | 'entregue' | 'pago' | 'cancelado'
  motivo_cancelamento: string | null
  tabela_preco_id: string | null
  condicao_pagamento_id: string | null
  // 0027: NOME da forma escolhida no fechamento; ausente/nula nos antigos.
  forma_pagamento?: string | null
  frete_tipo: 'CIF' | 'FOB' | 'REDESPACHO' | null
  transportadora: string | null
  previsao_entrega: string | null
  // 0040: quando o sub-pedido FECHOU e quando foi ENVIADO (pedido dela, 14/10).
  // Nulos nos antigos -- a tela mostra ali a ultima alteracao, marcada como tal.
  fechado_em?: string | null
  enviado_em?: string | null
  valor_total: number
  valor_faturado: number | null
  pdf_url: string | null
  observacoes: string | null
}

export interface ItemPedido extends RegistroBase {
  sub_pedido_id: string
  produto_id: string
  quantidade: number
  preco_unitario: number
  // ⚠ `quantidade` acima continua contando EMBALAGENS -- num produto `dz`, 5
  // sao 5 duzias, e sempre foi assim. As avulsas sao a dimensao NOVA.
  // Os tres congelam no momento da venda, como o preco_unitario ja congela.
  quantidade_avulsa?: number | null
  preco_avulso?: number | null
  unidades_por_embalagem?: number | null
  desconto_pct: number
  origem_preco: string // 'negociado' | 'tabela:<id>' | 'manual'
  total_item: number
  // Composicao do imposto (motor 0024), congelada no momento do calculo.
  // Todos nulos quando o item nao passou pelo motor -- representada sem
  // calcula_imposto, ou produto sem aliquota cadastrada para o destino.
  imposto_origem?: number | null
  imposto_destino_icms?: number | null
  imposto_diferenca_icms?: number | null
  imposto_ipi?: number | null
}

// Historico de reducao do pedido: uma linha por ajuste, nunca sobrescrita.
// Ciclo 1 do ciclo de vida do pedido (spec 2026-07-28). E o dado que a metrica
// de "venda nao concretizada" vai ler. A CULPA nao mora aqui: e deduzida do
// motivo em pedidos/ajustes.ts, para que reclassificar um motivo reorganize o
// historico inteiro junto.
export interface AjustePedido extends RegistroBase {
  sub_pedido_id: string
  escopo: 'item' | 'sub_pedido'
  item_pedido_id: string | null
  produto_id: string | null
  quantidade_antes: number | null
  quantidade_depois: number | null
  // A outra metade do lancamento: "devolveu 1 caixa e 2 unidades" e UM ajuste.
  quantidade_avulsa_antes?: number | null
  quantidade_avulsa_depois?: number | null
  valor_antes: number
  valor_depois: number
  motivo: string
}

export interface Visita extends RegistroBase {
  cliente_id: string
  data_hora: string
  resultado: 'com_pedido' | 'sem_pedido'
  pedido_id: string | null
  motivo_sem_pedido:
    | 'fechado' | 'sem_necessidade' | 'concorrente' | 'estoque_cheio' | 'preco_alto' | 'outro'
    | null
  observacao: string | null
}

// Compromisso avulso do vendedor (spec 2026-10-02, ciclo 2A). `data` e o dia
// LOCAL 'AAAA-MM-DD' e `hora` e 'HH:MM' -- nunca um ISO em UTC.
// Lista de segmentos do vendedor (0037, 11/10). O cliente guarda o nome.
export interface Segmento extends RegistroBase {
  nome: string
}

export interface Compromisso extends RegistroBase {
  data: string
  hora: string
  assunto: string
  cliente_id: string | null
  situacao: 'pendente' | 'feito'
  // 0034 (08/10): minutos de antecedencia do aviso; null = sem aviso. Opcional
  // no TIPO: o aparelho que ja tinha o compromisso antes da 0034 le `undefined`.
  aviso_minutos?: number | null
}

export interface Meta extends RegistroBase {
  escopo: string // 'pessoal' | 'representada:<id>'
  tipo:
    | 'valor_vendido'
    | 'positivacao'
    | 'itens_por_pedido'
    | 'cobertura_linhas'
    | 'comissao_necessaria'
    | 'faturamento_total'
  periodo_inicio: string
  periodo_fim: string
  valor_alvo: number
}

export interface ConfiguracaoMetas extends RegistroBase {
  pct_dinheiro: number
  pontos_cobertura: number
  passo_itens_por_pedido: number
  tipos_ativos: Meta['tipo'][]
}

export interface RegraComissao extends RegistroBase {
  representada_id: string
  tabela_preco_id: string | null
  tipo: 'pct_fixo' | 'pct_por_item'
  pct_padrao: number | null
  gatilho_liberacao: 'faturamento' | 'pagamento_cliente'
}

export interface ComissaoPercentual extends RegistroBase {
  regra_id: string
  alvo: string // 'produto:<id>' | 'linha:<nome>' | 'categoria:<nome>'
  pct: number
}

export interface LancamentoComissao extends RegistroBase {
  sub_pedido_id: string
  valor_previsto: number
  valor_recalculado: number | null
  status: 'prevista' | 'liberada' | 'recebida'
  data_liberacao: string | null
  data_recebimento: string | null
  valor_recebido: number | null
  // Conferencia ativa (spec 2026-07-30 §4.3): quanto da pendencia ja foi
  // acertado, COM SINAL -- positivo quando a fabrica pagou o que faltava,
  // negativo quando ela devolveu o que recebeu a mais.
  valor_acertado: number
  acerto_em: string | null
}

// Spec 2026-10-05: a diferenca do mes da fornecedora, quando o vendedor digita o
// TOTAL que a fabrica pagou. Mesmo sinal do saldoPendente (> 0 credito: a fabrica
// deve a ele; < 0 debito). Saldo em aberto = valor - valor_acertado.
export interface DiferencaComissao extends RegistroBase {
  representada_id: string
  mes: string // 'AAAA-MM' de referencia (o da baixa)
  valor: number
  data: string // dia da baixa, local
  valor_acertado: number
  acerto_em: string | null
}

export interface PerfilImportacao extends RegistroBase {
  representada_id: string
  tipo: 'produtos' | 'clientes'
  formato: 'xlsx' | 'csv' | 'pdf'
  mapa_colunas: Record<string, string>
  opcoes: Record<string, unknown>
}

export interface Importacao extends RegistroBase {
  perfil_id: string | null
  arquivo_path: string | null
  status: 'pendente' | 'processando' | 'aguardando_revisao' | 'aplicada' | 'erro'
  total_inseridos: number
  total_atualizados: number
  total_pulados: number
  erros: Array<{ linha: number; motivo: string }>
}

export interface Alerta extends RegistroBase {
  cliente_id: string
  tipo: 'recompra' | 'inativo' | 'cross_sell'
  payload: Record<string, unknown>
  gerado_em: string
  dispensado_em: string | null
}

// ---- Infra local (só existe no Dexie, nunca sobe) ----

export interface EntradaOutbox {
  id: string // uuid da mutacao
  tabela: string
  registro: Record<string, unknown> // linha completa a upsertar
  criado_em: string
  tentativas: number
  ultimo_erro: string | null
  ultimo_codigo: string | null // SQLSTATE/PostgREST: e ele que traduz a frase
  travado_em: string | null // preenchido = o app desistiu, nao tenta mais
}

export interface CursorSync {
  tabela: string
  cursor: string // maior atualizado_em ja puxado (ISO)
}

export const DIAS_SEMANA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const

export function agoraISO(): string {
  return new Date().toISOString()
}

export function novoRegistroBase(): Pick<RegistroBase, 'id' | 'criado_em' | 'atualizado_em' | 'apagado_em'> {
  const agora = agoraISO()
  return { id: novoId(), criado_em: agora, atualizado_em: agora, apagado_em: null }
}
